import { css } from '@emotion/react';
import { NString, Nullable, TextSubtitle2RBMixin, TextSubtitle3RMMixin, WidgetLayout } from '@mobalytics/shared';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { Marketing } from '../../../../marketing/marketing';
import { media } from '../../../../ui/global/media-query';
import { genPaymentBillingPath } from '../../../../utils/formatter/path.formatter';
import { PlanT } from '../../payment.types';
import { getMixpanelPlanName } from '../../utils/get-mixpanel-plan-name-utils';
import { PlanExtraFeature } from '../../../../__generated/treasury/types';
import { tftFreeLittleLegendPlanDetails } from '../../../../utils/formatter/images';
import { getMoneyBaseUnit } from '../../utils/get-money-base-unit.utils';
import { TFT_SET18_OFFER_COUPON } from '../../../../constants';
import { usePaymentFilters } from '../../hooks/payment-filters.hook';
import { Benefits } from './benefits/benefits.component';
import { NextPayment } from './next-payment/next-payment.component';
import { PlansList } from './plans-list/plans-list.component';
import { PricePerTimeframe } from './price-per-timeframe/price-per-timeframe.component';
import { TotalPrice } from './total-price/total-price.component';

interface Props {
  className?: string;
  productAbbreviation: string;
  plan: PlanT;
  currentPlanId: NString;
  plans: Nullable<PlanT[]>;
}

export const PlanDetails: React.FC<Props> = (props) => {
  const { className, productAbbreviation, plan, plans, currentPlanId } = props;
  const { t } = useTranslation();
  const { gameName } = usePaymentFilters();
  const [isPlanSelectionVisible, setIsPlanSelectionVisible] = React.useState(false);
  const { push } = useHistory();
  const { search } = useLocation();
  const isTftFreeEggPlan = Boolean(plan.planFeatures?.find((it) => it === PlanExtraFeature.EGG_SKIN));
  const isTftSet18OfferCoupon = plan.appliedCoupon === TFT_SET18_OFFER_COUPON;

  return (
    <WidgetLayout className={className} css={wrapperCss} bodyOverrideCss={widgetLayoutBodyCss}>
      {(isTftFreeEggPlan || isTftSet18OfferCoupon) && (
        <img src={tftFreeLittleLegendPlanDetails()} alt="" css={eggImageCss} />
      )}
      <div css={contentCss}>
        <div css={lineCss}>
          <p css={headerCss}>{t('Plan summary')}</p>
          {plans && plans.length > 1 && (
            <span
              css={[ctaCss, isPlanSelectionVisible && ctaActiveCss]}
              onClick={() => setIsPlanSelectionVisible(true)}
            >
              {t('Change')}
            </span>
          )}
        </div>
        <PricePerTimeframe
          currency={!plan.isTrial ? plan.currency : null}
          price={!plan.isTrial ? getMoneyBaseUnit(plan.finalPricePerMonth, true) : 0}
          timeframe={!plan.isTrial ? t('month') : t('trial')}
          css={pricePerTimeframeCss}
        />

        <Benefits plan={plan} />
        <div css={delimiterCss} />
        <TotalPrice plan={plan} />
      </div>
      <NextPayment css={nextPaymentCss} plan={plan} />
      {isPlanSelectionVisible && plans && (
        <PlansList
          plans={plans}
          selectedPlanId={plan.id}
          currentPlanId={currentPlanId}
          onSelect={(plan: PlanT) => {
            const mixpanelName = getMixpanelPlanName(plan, productAbbreviation, gameName, t);
            if (mixpanelName) {
              Marketing.planChanged(mixpanelName);
            }
            setIsPlanSelectionVisible(false);
            push(genPaymentBillingPath(plan.id, search));
          }}
          onClose={() => setIsPlanSelectionVisible(false)}
          css={plansCss}
        />
      )}
    </WidgetLayout>
  );
};

const wrapperCss = css`
  flex: 1 1 0%;
  max-width: 400px;
  width: 100%;
  min-height: 456px;
  overflow: hidden;
  ${media.medium} {
    min-height: auto;
  }
`;

const pricePerTimeframeCss = css`
  margin-top: 16px;
`;

const contentCss = css`
  position: relative;
`;

const widgetLayoutBodyCss = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  padding: 40px;
  flex: 1 1 auto;
  ${media.medium} {
    padding: 16px 12px;
  }
`;

const plansCss = css`
  position: absolute;
  left: 24px;
  right: 24px;
  top: 80px;
  ${media.medium} {
    top: 52px;
  }
`;

const lineCss = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const headerCss = css`
  ${TextSubtitle2RBMixin};
`;

const ctaCss = css`
  ${TextSubtitle3RMMixin};
  cursor: pointer;
  color: var(--general-grey-300);
`;

const ctaActiveCss = css`
  color: var(--general-white-500);
`;

const delimiterCss = css`
  width: 100%;
  height: 1px;
  background: var(--general-grey-300);
  opacity: 0.3;
  margin: 24px 0;
`;

const nextPaymentCss = css`
  ${media.medium} {
    display: none;
  }
`;

const eggImageCss = css`
  position: absolute;
  bottom: 0;
  right: 0;
`;
